import { useEffect, useRef, useState } from 'react';

type WebSocketCallbacks = {
  onOpen?: (event: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  onError?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onSocketCreated?: (socket: WebSocket) => void;
};

type WebSocketOptions = {
  /** Open a new socket after an unexpected close. Cleanup closes do not retry. */
  reconnect?: boolean;
};

export const useWebsockets = (
  relativePath: string | null | undefined,
  callbacks: WebSocketCallbacks = {},
  options: WebSocketOptions = {}
) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [attempt, setAttempt] = useState(0);
  const attemptRef = useRef(0);
  const reconnect = options.reconnect === true;

  // Store callbacks in a ref so changes don't recreate the websocket
  const callbacksRef = useRef<WebSocketCallbacks>(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!relativePath) {
      // Clean up any existing socket if url becomes null
      if (socket) {
        socket.close();
      }
      setSocket(null);
      return;
    }

    const rawBaseURL = import.meta.env.VITE_SOCKET_URL || '';
    // Local Daphne serves plain WS; .env often still has wss://localhost for parity with deployed envs.
    const baseURL =
      rawBaseURL && /localhost|127\.0\.0\.1/.test(rawBaseURL)
        ? rawBaseURL.replace(/^wss:/, 'ws:')
        : rawBaseURL || (import.meta.env.VITE_API_URL || '').replace(/^http/, 'ws');

    const fullURL =
      baseURL.endsWith('/') && relativePath.startsWith('/')
        ? baseURL + relativePath.substring(1)
        : baseURL.endsWith('/') || relativePath.startsWith('/')
          ? baseURL + relativePath
          : `${baseURL}/${relativePath}`;

    const ws = new WebSocket(fullURL);
    let closedByCleanup = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    ws.onopen = (event) => {
      attemptRef.current = 0;
      callbacksRef.current.onOpen?.(event);
    };

    ws.onmessage = (event) => {
      callbacksRef.current.onMessage?.(event);
    };

    ws.onerror = (event) => {
      callbacksRef.current.onError?.(event);
      ws.close();
    };

    ws.onclose = (event) => {
      callbacksRef.current.onClose?.(event);
      setSocket(null);
      if (!reconnect || closedByCleanup) return;
      const delay = Math.min(1000 * 2 ** attemptRef.current, 8000);
      attemptRef.current += 1;
      retryTimer = setTimeout(() => setAttempt((current) => current + 1), delay);
    };

    setSocket(ws);

    // Notify callback that socket has been created
    callbacksRef.current.onSocketCreated?.(ws);

    return () => {
      closedByCleanup = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws.close();
    };
    // `attempt` retries the same path after an unexpected close.
  }, [relativePath, attempt, reconnect]);

  return socket;
};
